import { useState } from "react";
import { ArrowRight, Mail, Phone, Globe, Instagram, MapPin } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useLang } from "../contexts/LangContext";
import { api, formatApiError } from "../lib/api";
import LocationMap from "../components/LocationMap";
import LaunchNotice from "../components/LaunchNotice";
import useSEO from "../lib/useSEO";

const initial = {
  name: "",
  email: "",
  phone: "",
  event_type: "",
  event_date: "",
  guests: "",
  children: "",
  concept: "",
  location: "",
  message: "",
};

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useSEO({
    title: "Kontakt & Anfrage — Pizza Catering Hoyerswerda & Lausitz | Tæste",
    description:
      "Sendet uns eure Anfrage für Pizza Catering aus Hoyerswerda und der Lausitz. Wir beraten euch persönlich zu Hochzeit, Geburtstag, Firmenfeier und Events. Standort: 02999 Lohsa.",
    keywords:
      "Kontakt Tæste, Pizza Catering Hoyerswerda anfragen, Catering Lausitz Anfrage, Catering Lohsa, Hochzeitscatering Lausitz buchen, mobiler Pizzaofen Catering",
    path: "/kontakt",
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t.contact.form.required;
    if (!form.email.trim()) e.email = t.contact.form.required;
    if (!form.event_type) e.event_type = t.contact.form.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success(t.contact.form.success);
      setForm(initial);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[hsl(var(--background))] border border-[hsl(var(--border))] px-4 py-3 text-base outline-none focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary))]/30 transition";

  return (
    <div data-testid="contact-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 md:py-28">
      <Toaster richColors position="top-center" />
      <div className="grid md:grid-cols-12 gap-16 items-start">
        <div className="md:col-span-5 md:sticky md:top-28">
          <p className="eyebrow">{t.contact.eyebrow}</p>
          <h1 className="font-display text-5xl md:text-6xl mt-3 leading-[1.02]">{t.contact.title}</h1>
          <p className="mt-6 text-[hsl(var(--muted-foreground))]">{t.contact.body}</p>

          <div className="mt-10 space-y-3 text-sm">
            <p className="eyebrow mb-2">{t.contact.info_title}</p>
            <a href={`mailto:${t.contact.info.email}`} className="flex items-center gap-3 hover:underline" data-testid="contact-info-email">
              <Mail size={16} /> {t.contact.info.email}
            </a>
            <a href="tel:+4915129402613" className="flex items-center gap-3 hover:underline" data-testid="contact-info-phone">
              <Phone size={16} /> {t.contact.info.phone}
            </a>
            <a href="https://taeste.de" className="flex items-center gap-3 hover:underline" data-testid="contact-info-web">
              <Globe size={16} /> {t.contact.info.web}
            </a>
            <a href="https://instagram.com/taeste.catering_streetfood" target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:underline" data-testid="contact-info-ig">
              <Instagram size={16} /> {t.contact.info.ig}
            </a>
            <p className="flex items-center gap-3" data-testid="contact-info-location">
              <MapPin size={16} /> {t.contact.info.location}
            </p>
          </div>
        </div>

        <form data-testid="contact-form" onSubmit={submit} className="md:col-span-7 space-y-5">
          <LaunchNotice
            testId="contact-launch-notice"
            eyebrow={t.contact.notice_eyebrow}
            body={t.contact.notice_body}
            className="mb-2"
          />
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="eyebrow block mb-2">{t.contact.form.name} *</label>
              <input data-testid="form-name" className={inputCls} value={form.name} onChange={(e) => upd("name", e.target.value)} />
              {errors.name && <p className="text-xs text-[hsl(var(--destructive))] mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="eyebrow block mb-2">{t.contact.form.email} *</label>
              <input data-testid="form-email" type="email" className={inputCls} value={form.email} onChange={(e) => upd("email", e.target.value)} />
              {errors.email && <p className="text-xs text-[hsl(var(--destructive))] mt-1">{errors.email}</p>}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="eyebrow block mb-2">{t.contact.form.phone}</label>
              <input data-testid="form-phone" className={inputCls} value={form.phone} onChange={(e) => upd("phone", e.target.value)} />
            </div>
            <div>
              <label className="eyebrow block mb-2">{t.contact.form.event_type} *</label>
              <select data-testid="form-event-type" className={inputCls} value={form.event_type} onChange={(e) => upd("event_type", e.target.value)}>
                <option value="">{t.contact.form.event_type_ph}</option>
                {t.contact.form.event_types.map((et) => (
                  <option key={et} value={et}>{et}</option>
                ))}
              </select>
              {errors.event_type && <p className="text-xs text-[hsl(var(--destructive))] mt-1">{errors.event_type}</p>}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <div>
              <label className="eyebrow block mb-2">{t.contact.form.event_date}</label>
              <input data-testid="form-event-date" type="date" className={inputCls} value={form.event_date} onChange={(e) => upd("event_date", e.target.value)} />
            </div>
            <div>
              <label className="eyebrow block mb-2">{t.contact.form.guests}</label>
              <input
                data-testid="form-guests"
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                placeholder={t.contact.form.guests_ph}
                className={inputCls}
                value={form.guests}
                onChange={(e) => upd("guests", e.target.value)}
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">{t.contact.form.children}</label>
              <input
                data-testid="form-children"
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                placeholder={t.contact.form.children_ph}
                className={inputCls}
                value={form.children}
                onChange={(e) => upd("children", e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-2">{t.contact.form.concept}</label>
            <select
              data-testid="form-concept"
              className={inputCls}
              value={form.concept}
              onChange={(e) => upd("concept", e.target.value)}
            >
              <option value="">{t.contact.form.concept_ph}</option>
              {t.contact.form.concepts.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="eyebrow block mb-2">{t.contact.form.location}</label>
            <input data-testid="form-location" className={inputCls} value={form.location} onChange={(e) => upd("location", e.target.value)} />
          </div>

          <div>
            <label className="eyebrow block mb-2">{t.contact.form.message}</label>
            <textarea data-testid="form-message" rows={5} placeholder={t.contact.form.message_ph} className={inputCls} value={form.message} onChange={(e) => upd("message", e.target.value)} />
          </div>

          <button
            data-testid="form-submit"
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 px-7 py-4 text-xs tracking-[0.2em] uppercase font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--accent))] disabled:opacity-60 rounded-sm"
          >
            {loading ? "…" : t.contact.form.submit} <ArrowRight size={14} />
          </button>
        </form>
      </div>

      {/* STANDORT / KARTE */}
      <section data-testid="contact-location" className="mt-20 md:mt-28 pt-16 border-t border-border">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <p className="eyebrow">{t.contact.location_title}</p>
            <h2 className="font-display text-3xl md:text-5xl mt-3 leading-[1.05]">{t.contact.info.location}</h2>
            <p className="mt-6 text-[hsl(var(--muted-foreground))] leading-relaxed">{t.contact.location_lead}</p>
          </div>
          <div className="md:col-span-8">
            <div className="relative aspect-[16/10] overflow-hidden border border-border bg-[hsl(var(--secondary))]">
              <LocationMap testId="contact-map" zoom={11} />
            </div>
            <a
              href="https://www.openstreetmap.org/#map=12/51.3833/14.4000"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-map-link"
              className="mt-3 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-semibold border-b border-[hsl(var(--primary))] pb-0.5"
            >
              Region auf OpenStreetMap ansehen
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
